import { ApolloError, useMutation } from "@apollo/client";
import { KIND } from "baseui/button";
import { ConfirmDialog } from "components/confirm-dialog";
import { Content } from "components/content";
import { FormRenderer } from "components/form-renderer";
import { Header } from "components/header";
import { NoPermissionsRedirect } from "components/no-permissions-redirect";
import { SlideVariant } from "components/slide-variant-selector/slide-variant-selector";
import { useAuth } from "contexts/auth-context";
import { useLoading } from "contexts/loading-context";
import { useProject } from "contexts/project-context";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { DeviceFloppy, FileOff, Note } from "tabler-icons-react";
import { PERMISSIONS } from "utils/permissions";
import { scrollOnError } from "utils/scrollOnError";

import { SLIDES_FIELDS, SlidesFormInputs } from "../slides.form";
import { SLIDES_CREATE } from "../slides.gql";

export default function SlidesCreate(): React.ReactElement {
  const [isCancelConfirmDialogOpen, setIsCancelConfirmDialogOpen] = useState(
    false
  );
  const [isClearConfirmDialogOpen, setIsClearConfirmDialogOpen] = useState(
    false
  );
  const [errorTimeStamp, setErrorTimeStamp] = useState<number>();
  const { isLoading, setIsLoading } = useLoading();
  const { enqueueSnackbar } = useSnackbar();
  const { checkPermission } = useAuth();
  const { activeProjectId } = useProject();
  const history = useHistory();

  const {
    control,
    formState: { errors, isDirty, ...formState },
    handleSubmit,
    reset,
    setValue,
    watch,
    ...methods
  } = useForm<SlidesFormInputs>({
    defaultValues: {
      title: "",
      lead: "",
      isVisible: false,
      position: "",
      slideVariant: { type: SlideVariant.Post, value: "", buttonLabel: "" },
    },
  });

  const watchProject = watch("project");

  useEffect(() => {
    setValue("language", []);

    setValue("slideVariant", {
      type: watch("slideVariant")?.type,
      value: "",
      buttonLabel: "",
    });
  }, [activeProjectId, watchProject]);

  const [createSlide, { error }] = useMutation(SLIDES_CREATE);

  const onError = (errors: Record<string, unknown>, e: any) =>
    setErrorTimeStamp(e.timeStamp);

  useEffect(() => {
    if (errors) scrollOnError(errors, SLIDES_FIELDS, "create");
  }, [errorTimeStamp]);

  const onSubmit = async ({
    project,
    language,
    isVisible,
    slideVariant,
    title,
    lead,
    image,
  }: SlidesFormInputs): Promise<void> => {
    setIsLoading(true);

    try {
      const { data } = await createSlide({
        variables: {
          slideCreateInput: {
            projectId: activeProjectId || project?.[0]?.id,
            language: language?.[0]?.id,
            isVisible: !!isVisible,
            ...(slideVariant?.type === SlideVariant.Article && {
              articleId: parseInt(slideVariant.value as string),
            }),
            ...(slideVariant?.type === SlideVariant.Post && {
              postId: parseInt(slideVariant.value as string),
            }),
            ...(slideVariant?.type === SlideVariant.StaticPage && {
              staticPageId: parseInt(slideVariant.value as string),
            }),
            ...(slideVariant?.type === SlideVariant.Custom && {
              url: slideVariant.value,
              buttonLabel: slideVariant.buttonLabel,
            }),
            title,
            lead,
            image,
          },
        },
      });

      enqueueSnackbar({
        message: "Utworzono pomyślnie",
        variant: "success",
      });
      history.push(`/slides/${data?.slideCreate?.id}`);
    } catch (error: unknown) {
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!checkPermission(PERMISSIONS.slide.create))
    return <NoPermissionsRedirect />;

  return (
    <article>
      <Header
        title="Nowy slajd"
        labels={["Slajdy", "Tworzenie"]}
        goBackOption
        buttons={[
          {
            label: "Wyczyść",
            kind: KIND.secondary,
            startEnhancer: <Note size={18} />,
            disabled: isLoading,
            onClick: () => {
              setIsClearConfirmDialogOpen(true);
            },
          },
          {
            label: "Anuluj",
            kind: KIND.secondary,
            startEnhancer: <FileOff size={18} />,
            disabled: isLoading,
            onClick: () =>
              isDirty ? setIsCancelConfirmDialogOpen(true) : history.goBack(),
          },
          {
            label: "Utwórz",
            kind: KIND.primary,
            startEnhancer: <DeviceFloppy size={18} />,
            onClick: handleSubmit(onSubmit, onError),
            disabled: isLoading,
            isLoading: isLoading,
            type: "submit",
            formId: "createSlide",
          },
        ]}
      />
      <Content>
        <FormProvider<SlidesFormInputs>
          control={control}
          formState={{ errors, isDirty, ...formState }}
          handleSubmit={handleSubmit}
          reset={reset}
          watch={watch}
          setValue={setValue}
          {...methods}
        >
          <FormRenderer<SlidesFormInputs>
            id="createSlide"
            onSubmit={handleSubmit(onSubmit)}
            type="create"
            isLoading={isLoading}
            error={error}
            fields={SLIDES_FIELDS}
          />
        </FormProvider>
        <ConfirmDialog
          isOpen={isCancelConfirmDialogOpen}
          label="Anulowanie tworzenia nowego slajdu"
          close={() => setIsCancelConfirmDialogOpen(false)}
          confirm={() => history.goBack()}
        />
        <ConfirmDialog
          isOpen={isClearConfirmDialogOpen}
          label="Wyczyszczenie formularza"
          close={() => setIsClearConfirmDialogOpen(false)}
          confirm={() => {
            reset();
            setIsClearConfirmDialogOpen(false);
          }}
        />
      </Content>
    </article>
  );
}
